import { useState, useEffect } from "react";
import "./FormCollect.css";
import { useSelector } from "react-redux";
import { selectUIStates, selectUser, setEditingForm, setUIStates } from "../../redux/features/appData";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ResponseContent } from "../response-content/ResponseContent";
import { toastFail, toastSuccess } from "../../config";
import toast from "react-hot-toast";
import { Question, CreateFormResponseCompletePayload, FormExtended, iconPath, QuestionComment, QuestionType, FormSetting } from "effno-core";
import { apiCreateFormResponseComplete, apiGetFormResponseCountForForm } from "../../services/ResponseService";
import { apiGetForm, apiGetFormFromPublicLink, apiGetFormParent } from "../../services/FormService";
import { calculateWorkflowJump, workflowJumpResponseType } from "../workflow/questionWorkflowRouter";
import { ICONS } from "../shared/media";
import { useDispatch } from "react-redux";
import { FormPasswordModal } from "./FormPasswordModal";
import { set } from "react-datepicker/dist/date_utils";
import { isMobile } from "../../utils/screens";
import { Laptop, Link, RotateCcw, Smartphone, SquareX, X } from "lucide-react";
import { MySwitch } from "../form-widgets/MySwitch";
import clipboardCopy from "clipboard-copy";
import config from "environment";

const clientUrl = config.clientUrl;

const DEFAULT_THEME = "light-mode";

export type FormClosedReason = "closed" | "expired" | "full" | "other" | undefined;

type FormCollectProps = {
    isPreview: boolean;
}

export const FormCollect = ({isPreview = false} : FormCollectProps) => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const params = useParams();
    const [searchParams] = useSearchParams();

    const uiStates = useSelector(selectUIStates);

    // const storeUser = useSelector(selectUser);
    
    const [questionIndex, setQuestionIndex] = useState<number>(0);
    const [formResponseValues, setFormResponseValues] = useState<Map<string, string> | null>(null);
    const [isForcedEnd, setIsForcedEnd] = useState<boolean>(false);
    const [publicLink, setPublicLink] = useState<string | undefined>(undefined);
    const [formId, setFormId] = useState<string | undefined>(undefined);
    const [form, setForm] = useState<FormExtended | undefined>(undefined);
    const [formSettings, setFormSettings] = useState<FormSetting | undefined>(undefined);
    const [mobileLayout, setMobileLayout] = useState<boolean>(false);
    const [commentMode, setCommentMode] = useState<boolean>(false);
    const [needsPassword, setNeedsPassword] = useState(false);
    const [formResponseCount, setFormResponseCount] = useState<number | undefined>(undefined);
    const [startingQuestionId, setStartingQuestionId] = useState<string | null>(null);
    const [didStartingJump, setDidStartingJump] = useState<boolean>(false);

    useEffect(() => {
        if (isPreview) {
            document.addEventListener("keydown", (e) => {
                if (e.key === "Escape") {
                    e.stopPropagation();

                    // if (!commentMode) {
                    //     handleStop();
                    // }

                    console.log("[FormCollect] escape key");
                    setCommentMode(false);
                }
            })

            return () => {
                document.removeEventListener("keydown", () => {});
            }
        }

        setMobileLayout(isMobile());
        // setMobileLayout(true);

        console.log("[FormCollect] isMobile: ", isMobile());
    }, []);

    useEffect(() => {
        setStartingQuestionId(searchParams.get("question"));
    }, [searchParams])

    useEffect(() => {
        if (startingQuestionId && form && form.questions) {
            const questionOrder = form?.questionOrder ? JSON.parse(form.questionOrder) : [];
            const index = questionOrder.indexOf(startingQuestionId);
            
            if (index === -1) {
                toast("Error finding starting question", toastFail);
                return;
            }

            setQuestionIndex(index);
            setDidStartingJump(true);
        }
    }, [startingQuestionId, form])

    useEffect(() => {
        // could be either if preview or collect
        setPublicLink(params.publicLink);
        setFormId(params.formId);
        setFormResponseCount(undefined);
        setFormResponseValues(new Map<string, string>());
    }, [params]);

    useEffect(() => {
        if (!form && publicLink) {
            (async () => {
                const theForm = await apiGetFormFromPublicLink(publicLink);

                if (!theForm) {
                    toast("Error getting form", toastFail);
                    return;
                }

                const count = await apiGetFormResponseCountForForm(theForm.id);
            
                if (count === null) {
                    toast("Error getting form response count", toastFail);
                    return;
                }

                setFormResponseCount(count);
                setFormResponseValues(new Map<string, string>());
                setForm(theForm)
                setFormId(theForm.id);

                // get settings from parent
                const parentForm = await apiGetFormParent(theForm.id);

                if (!parentForm) {
                    toast("Error getting parent form", toastFail);
                    return;
                }

                const theSettings = parentForm.formSettings;
                setFormSettings(theSettings);

                 // do we need a password
                 if (theSettings && theSettings.passwordProtected) {
                     setNeedsPassword(true);
                 }
            })()
        }
    }, [publicLink])

    useEffect(() => {
        if (!form && formId) {
            (async () => {
                const theForm = await apiGetForm(formId);

                if (!theForm) {
                    toast("Error getting form", toastFail);
                    return;
                }

                console.log("[FormCollect] theForm: ", theForm);

                setFormResponseValues(new Map<string, string>());
                setForm(theForm);

            })()
        }
    }, [formId])

    const getQuestionFromQuestionIndex = (theIndex: number) => {
        if (!form || !form.questions) {
            return null;
        }

        const questionOrder = form?.questionOrder ? JSON.parse(form.questionOrder) : [];
        const index = form.questions.findIndex((q: Question) => q.id === questionOrder[theIndex]);

        if (index === -1) {
            return null;
        }

        return form.questions[index];
    }

    const onNext = () => {  
        console.log("[FormCollect] onNext");
        console.log("[FormCollect] questionIndex: ", questionIndex);
        console.log("[FormCollect] form: ", form);
        console.log("[FormCollect] preview: ", isPreview);
        
        if (!form || !formResponseValues || !form.questions) {
            return;
        }

        const questionOrder = form?.questionOrder ? JSON.parse(form.questionOrder) : [];
        const question =  getQuestionFromQuestionIndex(questionIndex);

        // if its a preview just go forward or backward in question order
        if (isPreview) {
            if (questionIndex === questionOrder.length -1 ) {
                setIsForcedEnd(true);
            } else {
                setQuestionIndex(questionIndex + 1);
            }

            return;
        }

        const workflowJump = question ? calculateWorkflowJump(
            question,
            formResponseValues?.get(questionOrder[questionIndex]) || "{}"
        ) : { isError: true, errorText: "No question found", questionId: null } as workflowJumpResponseType;
        
        console.log("[FormCollect] workflowJump: ", workflowJump);

        if (workflowJump.isError) {
            toast("Workflow error: " + workflowJump.errorText, toastFail);
            console.log("[FormPreview] workflow error: ", workflowJump.errorText);
            return;
        }

        if (!workflowJump.isError && workflowJump.questionId) {
            const nextQuestionIndex = form.questions.findIndex((question: Question) => {
                return question.id === workflowJump.questionId
            });

            if (nextQuestionIndex === -1) {
                toast("Can't find question id", toastFail);
                console.log("[FormCollect] Can't find question id: ", workflowJump.questionId);
                return;
            }

            setQuestionIndex(nextQuestionIndex);
            return;
        }

        // if no workflow jump, just go to the next question
        if (questionIndex < form.questions.length - 1) {
            setQuestionIndex(questionIndex + 1);
        }
    }

    const onPrev = () => {
        console.log("[FormCollect] onPrev");

        if (!form) {
            return;
        }

        if (questionIndex > 0) {
            setQuestionIndex(questionIndex - 1);
        }
    }

    const onSubmit = (responseValues: string) => {
        console.log("[FormCollect/onSubmit] onSubmit: ", responseValues);
        console.log("[FormCollect/onSubmit] questionIndex: ", questionIndex);

        // alert("onSubmit");

        if (!formResponseValues || !form) {
            return;
        }

        const theQuestion = getQuestionFromQuestionIndex(questionIndex);

        if (theQuestion) {
            formResponseValues.set(theQuestion.id, responseValues);
        }

        // there are a number of ways this can go
        // 1. if this is the last one, there are no end questions, its a submit by default
        // 2. if the next one is an end, its a submit
        // 3. go to the next one

        const questionOrder = form?.questionOrder ? JSON.parse(form.questionOrder) : [];

        if (questionIndex === questionOrder.length - 1) {
            setIsForcedEnd(true);
            console.log("[FormCollect/onSubmit] submitting because we are at the end with no end card: ", formResponseValues);

            (async () => {
                // const questionSettings = form.questions.map((q: Question) => q.settings);
                // const questionIds = form.questions.map((q: Question) => q.id);

                // const payload = {
                //     formId: formId, 
                //     questionIds,
                //     questionSettings,
                //     responses: formResponseValues
                // } as CreateFormResponseCompletePayload;

                // const formResponse = await apiCreateFormResponseComplete(payload)

                // if (!formResponse) {
                //     toast("Error submitting form", toastFail);
                //     return;
                // }

                // toast("Form submitted.", toastSuccess) 
                await saveResponse();
            })();

            return;
        }

        const index = (form && form.questions) ?
            form.questions.findIndex((q: Question) => q.id === questionOrder[questionIndex]) : -1;
        
        if (index === -1) {
            toast("Error finding next question in order", toastFail);
            console.log("[FormCollect/onSubmit] Error finding next question in order");

            return;
        }

        const question = getQuestionFromQuestionIndex(questionIndex);

        const workflowJump = question ? calculateWorkflowJump(
            question,
            formResponseValues?.get(questionOrder[questionIndex]) || "{}"
        ) : { isError: true, errorText: "No question found", questionId: null } as workflowJumpResponseType;
        
        
        console.log("[FormCollect/onSubmit] workflowJump: ", workflowJump);

        if (workflowJump.isError) {
            toast("Workflow error: " + workflowJump.errorText, toastFail);
            console.log("[FormCollect/onSubmit] workflow error: ", workflowJump.errorText);
            return;
        }

        // if there a jump, if its an end submit, otherwise just go to it
        if (!workflowJump.isError && workflowJump.questionId) {
            const nextQuestionIndex = questionOrder.findIndex((id: string) => {
                return id === workflowJump.questionId
            });

            if (nextQuestionIndex === -1) {
                toast("Can't find question id", toastFail);
                console.log("[FormCollect/onSubmit] Can't find question id: ", workflowJump.questionId);
                return;
            }

            // make this the next question
            setQuestionIndex(nextQuestionIndex);

            const index = form && form.questions ?
                form.questions.findIndex((q: Question) => q.id === workflowJump.questionId) : -1;

            if (index === -1) {
                toast("Error finding next question in order", toastFail);
                console.log("[FormCollect/onSubmit] Error finding next question in order");

                return;
            }

            // if its an end, submit before going to it
            if (form.questions[index].type === "END") {
                console.log("[FormCollect/onSubmit] submitting as jump question is an end: ", formResponseValues);

                (async () => {
                    // const questionSettings = form.questions.map((q: Question) => q.settings);
                    // const questionIds = form.questions.map((q: Question) => q.id);

                    // const payload = {
                    //     formId: formId, 
                    //     questionIds,
                    //     questionSettings,
                    //     responses: formResponseValues
                    // } as CreateFormResponseCompletePayload;

                    // const formResponse = await apiCreateFormResponseComplete(payload)

                    // if (!formResponse) {
                    //     toast("Error submitting form", toastFail);
                    //     return;
                    // }

                    // toast("Form submitted.", toastSuccess) 
                    await saveResponse()
                    
                })();
                return;

            }

            console.log("[FormCollect/onSubmit] going to next question in workflow jump: ", nextQuestionIndex);

        } else {
            // there is no jump just got to the next index in the order
            console.log("[FormCollect/onSubmit] last branch");
            const questionOrder = form?.questionOrder ? JSON.parse(form.questionOrder) : [];
            console.log("[FormCollect/onSubmit] questionOrder: ", questionOrder);
            console.log("[FormCollect/onSubmit] current questionId: ", questionOrder[questionIndex]);
            console.log("[FormCollect/onSubmit] going to questionId: ", questionOrder[questionIndex + 1]);

            setQuestionIndex(questionIndex + 1);

            const question = getQuestionFromQuestionIndex(questionIndex + 1)

            if (question?.type === "END") {
                console.log("[FormCollect/onSubmit] submitting as next default question is an end: ", formResponseValues);

                (async () => {
                    await saveResponse()
                })();
            }
        }
        
       return;
    }

    // based on what has been saved in formResponseValues we prep the response
    const saveResponse = async () => {
        if (isPreview) {
            console.log("[SaveResponse] would have saved but its a preview so won't");
            return;
        }

        console.log("[FormCollect/saveResponse] formResponseValues: ", formResponseValues);

        if (!formResponseValues || !form) {
            return;
        }

        const questionSettings : string[] = [];
        const questionIds : string[] = [];
        const responseValues : string[] = [];

        formResponseValues.forEach((value, key) => {
            questionIds.push(key);

            const index = form.questions.findIndex((q: Question) => q.id === key);

            if (index === -1) {
                toast("Error finding question while saving", toastFail);
                return;
            }

            questionSettings.push(form.questions[index].settings);

            responseValues.push(value);
        });
      

        // NR fix this as it needs to use order mapping
        const payload = {
            formId: formId, 
            questionIds,
            questionSettings,
            responses: responseValues
        } as CreateFormResponseCompletePayload;

        const formResponse = await apiCreateFormResponseComplete(payload)

        if (!formResponse) {
            toast("Error submitting form", toastFail);
            return;
        }

        toast("Form submitted.", toastSuccess) 
    }

    const handleStop = async () => {
        // in this case, we need to reload the form because some comments might have
        // happened and they would not have been put into redux
        if (formId) {
            const newForm = await apiGetForm(formId);

            if (!newForm) {
                toast("Error getting form", toastFail);
                return;
            }

            dispatch(setEditingForm(newForm));
        }

        dispatch(setUIStates({...uiStates,
            viewingBuild: true,
            viewingDesign: false,
            viewingSettings: false,
            viewingPreview: false,
            viewingComments: false,
            viewingResponses: false,
            viewingShare: false,
        }));

        navigate("/editor")
    }

    const handleRestart = () => {
        if (form?.questions) {
            setFormResponseValues(new Map<string, string>());
        }

        setQuestionIndex(0);
        setIsForcedEnd(false);
    } 

    const handleCopyLink = async () => {
        if (!form) {
            return;
        }

        const previewLink = clientUrl + "/preview/" + form.id;

        if (!previewLink) {
            toast("Error getting public link", toastFail);
            return;
        }

        await clipboardCopy(previewLink);
        toast("Preview link copied", toastSuccess);
    }

    const onNewComment = (newComment: QuestionComment) => {
        if (!form) {
            return;
        }

        // update form with the new question and the new comment
        const theQuestion = getQuestionFromQuestionIndex(questionIndex);

        if (!theQuestion) {
            toast("Error completing comment, can't find question", toastFail);
            return;
        }

        const newQuestions = [...form.questions];
        const newComments = [...theQuestion.comments]
        newComments.push(newComment)
        const newQuestion = {...newQuestions[questionIndex], comments: newComments}
        newQuestions[questionIndex] = newQuestion;
        const newForm = {...form, questions: newQuestions};
        setForm(newForm);
    }

    const onCompletedComment = (commentId: string) => {
        if (!form) {
            return;
        }

        // update form with the new question with the new comment changes
        const theQuestion = getQuestionFromQuestionIndex(questionIndex);

        if (!theQuestion) {
            toast("Error completing comment, can't find question", toastFail);
            return;
        }

        const remainingComments = theQuestion.comments.filter((c) => {
            return (c.id !== commentId) && (c.parentQuestionCommentId !== commentId)
        })

        const newComments = [...remainingComments]
        const newQuestion = {...theQuestion, comments: newComments};
        const newQuestions = [...form.questions];
        newQuestions[questionIndex] = newQuestion;
        const newForm = {...form, questions: newQuestions};
        setForm(newForm);
    }

    const getCurrentQuestionInfo = () => {
        const questionOrder = form?.questionOrder ? JSON.parse(form.questionOrder) : [];
        const index = (form && form.questions) ? 
            form.questions.findIndex((q: Question) => q.id === questionOrder[questionIndex]) : -1;
        const question: Question | null = (form && index !== -1) ? form.questions[index] : null;
        const isFirst = questionIndex === 0;
        const isLast = form?.questions ? (questionIndex === form?.questions.length - 1) : false;

        return {question, isFirst, isLast};
    }

    const getProgressPercentage = () : number | undefined => {
        if (!form || !formSettings || !formSettings.progressBar) {
            return undefined;
        }
        
        let nonEndQuestions = 0;
    
        form.questions.forEach((q: Question) => {
            if (q.type !== QuestionType.END) {
                nonEndQuestions++;
            }
        })

        if (nonEndQuestions === 0) {
            return undefined;
        }

        return Math.floor((questionIndex / nonEndQuestions) * 100) + 1;
    }

    const getFormClosed = () : { isClosed: boolean, closedReason: FormClosedReason } => {
        if (!form || !formSettings) {
            return {isClosed: false, closedReason: undefined};
        }

        if (formSettings.closed) {
            return {isClosed: true, closedReason: "closed"};
        }

        if (formSettings.closeDate && new Date() > new Date(formSettings.closeDate)) {
            return {isClosed: true, closedReason: "expired"};
        }
             
        if (formSettings.submissionLimit && 
            formResponseCount !== undefined &&
            formResponseCount >= formSettings.submissionLimit) {
                return {isClosed: true, closedReason: "full"};
        }

        return {isClosed: false, closedReason: undefined}
    }

    document.body.className = DEFAULT_THEME;

    const {question, isFirst, isLast} = getCurrentQuestionInfo();

    const progressPercentage = getProgressPercentage();

    const {isClosed, closedReason} = getFormClosed();
    const interuptWithPassword = !isPreview && needsPassword;

    // console.log("[FormCollect] formId: ", formId);
    // console.log("[FormCollect] publiclink: ", publicLink);
    // console.log("[FormCollect] progresspercentage: ", progressPercentage);
    // console.log("[FormCollect] needs password: ", interuptWithPassword);
    console.log("[FormCollect] isClosed: ", isClosed);
    console.log("[FormCollect] closedReason: ", closedReason);
    console.log("[FormCollect] didStartingJump: ", didStartingJump);

    return (
        <>
            <div id="form-collect">
                {!interuptWithPassword && question && 
                    <ResponseContent
                        isPreview={isPreview}
                        isClosed={isClosed}
                        closedReason={closedReason}
                        commentMode={commentMode}
                        isMobileLayout={mobileLayout}
                        isForcedEnd={isForcedEnd}
                        onNext={(isLast && !isPreview) ? undefined : onNext}
                        onPrev={isFirst ? undefined : onPrev}
                        onSubmit={onSubmit}
                        onNewComment={onNewComment}
                        onCompletedComment={onCompletedComment}
                        form={form}
                        question={!isForcedEnd ? question : undefined}
                        // responseValues={formResponseValues?.get(questionOrder[questionIndex]) || "{}"}
                        responseValues={formResponseValues?.get(question.id)|| "{}"}
                        progressPercentage={progressPercentage}
                    />
                }

                {interuptWithPassword &&
                    <FormPasswordModal 
                        isOpen={interuptWithPassword}
                        form={form}
                        onSuccess={()=>setNeedsPassword(false)}
                    />

                }

                {isPreview &&
                    <div className="form-collect-controls">

                        <RotateCcw 
                            onClick={handleRestart}    
                            size={24}
                            style={{cursor: "pointer"}}
                        />

                        {mobileLayout && 
                            <Laptop 
                                onClick={() => {
                                    setCommentMode(false);
                                    setMobileLayout(false)
                                }}
                                size={24}
                                style={{cursor: "pointer"}}
                            />
                        }

                        {!mobileLayout &&
                            <Smartphone 
                                onClick={() => {
                                    setCommentMode(false);
                                    setMobileLayout(true)
                                }}
                                size={24}
                                style={{cursor: "pointer"}}
                            />
                        }

                        <Link 
                            onClick={handleCopyLink}    
                            size={22}
                            style={{cursor: "pointer"}}
                        />
                        
                        <div className="form-collect-controls-vertical">
                            {}
                        </div>

                        <div className="form-collect-controls-comment-mode">
                            <div className="form-collect-controls-label">
                                Comment Mode: 
                            </div>
                            <MySwitch 
                                id="commentMode"
                                checked={!mobileLayout && commentMode}
                                onChange={() => {
                                    if (mobileLayout) {
                                        toast("Comments available only in laptop view", toastSuccess)
                                    } else {
                                        setCommentMode(!commentMode)                      
                                    }                                
                                }}
                            />
                        </div>

                        <div className="form-collect-controls-vertical">
                            {}
                        </div>

                        <X 
                            onClick={handleStop}    
                            size={24}
                            style={{cursor: "pointer"}}
                        />
                      
                    </div>
                }   
                
            </div>
        </>
     )
}
